import { IOfferGoal, IOfferV2 } from '@/api/types/offers';
import {
    ICashbackOfferBonus,
    IContestCashbackGoal,
    IContestSpecialPeriod,
} from '@/components_v3/affiliate/contest/entities/cashback-offer/model/types';
import { parseOfferMoneyAmount } from '@/components_v3/affiliate/contest/shared/lib/parse-offer-money.lib';

export interface IContestOfferGoalsSource {
    goals?: IOfferGoal[];
}

export interface IExtractedSpecialPeriod {
    enabled: boolean;
    period: IContestSpecialPeriod | null;
}

/** ID цели для contest API: legacy_id → hasoffers_id → legacy_id: 0 (без hasoffers_id в ответе offers). */
export function resolveContestGoalId(goal: IOfferGoal): number | null {
    if (goal.legacy_id != null && goal.legacy_id !== 0) {
        return goal.legacy_id;
    }

    if (typeof goal.hasoffers_id === 'number' && !Number.isNaN(goal.hasoffers_id)) {
        return goal.hasoffers_id;
    }

    if (goal.legacy_id === 0) {
        return 0;
    }

    return null;
}

export function isContestGoalIdValid(goalId: number | null | undefined): goalId is number {
    return typeof goalId === 'number' && !Number.isNaN(goalId);
}

function buildGoalMetaByLegacyId(
    offerData: IContestOfferGoalsSource | null,
): Map<number, IOfferGoal> {
    const goalByLegacyId = new Map<number, IOfferGoal>();

    for (const goal of offerData?.goals ?? []) {
        if (goal.active === false) {
            continue;
        }

        const legacyId = resolveContestGoalId(goal);

        if (!isContestGoalIdValid(legacyId)) {
            continue;
        }

        goalByLegacyId.set(legacyId, goal);
    }

    return goalByLegacyId;
}

/**
 * Спец-период на оффере — один датапикер на все цели.
 * В API даты лежат в bonuses[].periods[0] у каждой цели; при GET берём from/to
 * у первой цели с периодом (у всех они совпадают).
 */
export function extractSpecialPeriodFromBonuses(bonuses: ICashbackOfferBonus[]): IExtractedSpecialPeriod {
    for (const bonus of bonuses) {
        const period = bonus.periods?.[0];

        if (period?.from && period?.to) {
            return {
                enabled: true,
                period: {
                    from: period.from,
                    to: period.to,
                },
            };
        }
    }

    return {
        enabled: false,
        period: null,
    };
}

function mapBonusesToGoals(
    offerData: IContestOfferGoalsSource | IOfferV2 | null,
    bonuses: ICashbackOfferBonus[],
): IContestCashbackGoal[] {
    const goalByLegacyId = buildGoalMetaByLegacyId(offerData);

    return bonuses
        .filter((bonus) => isContestGoalIdValid(bonus.goal_id))
        .map((bonus) => {
            const goal = goalByLegacyId.get(bonus.goal_id);
            const period = bonus.periods?.[0];

            return {
                id: bonus.goal_id,
                legacy_id: bonus.goal_id,
                name: goal?.name ?? '—',
                bonus: parseOfferMoneyAmount(bonus.bonus),
                periodBonus: period ? parseOfferMoneyAmount(period.bonus) : null,
            };
        });
}

/**
 * Сохранённый оффер (GET / просмотр / редактирование): только строки из bonuses cashback-offers.
 * Пустой bonuses → пустая таблица (без подмешивания целей из offers/v2).
 */
export function mapCashbackOfferGoals(
    offerData: IContestOfferGoalsSource | IOfferV2 | null,
    bonuses: ICashbackOfferBonus[] = [],
): IContestCashbackGoal[] {
    if (!bonuses.length) {
        return [];
    }

    return mapBonusesToGoals(offerData, bonuses);
}

/**
 * Форма добавления оффера (bonuses ещё нет в cashback-offers).
 * Active цели из offers/v2, включая legacy_id: 0; bonus/periodBonus — null
 * (дефолты бонусов конкурса employees API не отдаёт до add;
 * payout.amount не подставляем — это не бонус конкурса).
 */
export function mapContestOfferGoalsForAdd(
    offerData: IContestOfferGoalsSource | IOfferV2 | null,
): IContestCashbackGoal[] {
    if (!offerData?.goals?.length) {
        return [];
    }

    const result: IContestCashbackGoal[] = [];

    for (const goal of offerData.goals) {
        if (goal.active === false) {
            continue;
        }

        const legacyId = resolveContestGoalId(goal);

        if (!isContestGoalIdValid(legacyId)) {
            continue;
        }

        result.push({
            id: legacyId,
            legacy_id: legacyId,
            name: goal.name,
            bonus: null,
            periodBonus: null,
        });
    }

    return result;
}
